/* eslint-disable react/require-default-props, react/forbid-prop-types */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import Modal from '../../../../components/Modal/Modal';
import { GhostButton, PrimaryButton } from '../../../../styles/ui/Button';
import { restoreScheduleItem } from '../../../../services/routing/routingApi';

// =============================================================================
// Dialog de confirmação da RESTAURAÇÃO de item cancelado (handoff 0059) —
// reverte o soft delete: CANCELADO → PENDENTE, de volta ao balde NÃO
// ROTEIRIZADOS. Espelho do CancelItemDialog (mesma anatomia, ação inversa).
//
// Reaproveitado por todas as superfícies de restore: a ficha (RoutingItemShow),
// o modal "Itens excluídos" do board (CancelledItemsModal) e os atalhos dos
// erros de duplicata (ManualEntryTab, RescheduleDialog). Mexer aqui cobre tudo.
//
// A confirmação existe porque restaurar APAGA os metadados do cancelamento
// (data/autor/motivo) — decisão 2026-07-07, sem histórico de reversão. Um
// clique acidental perde o registro de quem/por que excluiu.
//
// Permissão: routing:schedule_item:cancel (exclusiva da roteirização — o pai
// só renderiza o gatilho pra quem tem; o backend responde 403 sem ela).
// =============================================================================

const Body = styled.div`
   font-size: 0.85rem;
   line-height: 1.5;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#222'};
   padding: 4px 0;
`;

const QuestionLine = styled.p`
   margin: 0 0 12px;

   strong {
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   }
`;

const HintBox = styled.div`
   background: rgba(255, 193, 7, 0.1);
   border: 1px solid rgba(255, 193, 7, 0.4);
   border-radius: 8px;
   padding: 10px 12px;
   font-size: 0.78rem;
   color: #7d5a00;
   line-height: 1.5;
`;

const ButtonRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 16px;
`;

export default function RestoreItemDialog({
   isOpen,
   onClose,
   item,
   onRestored,
}) {
   const [isSubmitting, setIsSubmitting] = useState(false);

   const mountedRef = useRef(true);
   useEffect(() => {
      mountedRef.current = true;
      return () => {
         mountedRef.current = false;
      };
   }, []);

   const handleClose = useCallback(() => {
      if (isSubmitting) return;
      onClose?.();
   }, [isSubmitting, onClose]);

   const handleConfirm = async () => {
      if (isSubmitting || !item?.id) return;

      setIsSubmitting(true);
      try {
         const result = await restoreScheduleItem(item.id);
         toast.success(result.message || 'Item restaurado com sucesso.');
         onRestored?.(result.scheduleItem);
         onClose?.();
      } catch (err) {
         const apiMessage =
            err?.response?.data?.errors?.[0]?.message ??
            err?.response?.data?.message ??
            err?.message ??
            'Erro ao restaurar item.';
         toast.error(apiMessage);

         // 422 invalid_state_for_restore é corrida: o item deixou de ser
         // CANCELADO (outro operador restaurou/estado mudou). O pai re-busca
         // pra sincronizar a UI com o estado real — mesmo padrão do
         // CancelItemDialog.
         if (err?.response?.status === 422) {
            onRestored?.(null);
         }
      } finally {
         if (mountedRef.current) setIsSubmitting(false);
      }
   };

   if (!isOpen || !item) return null;

   return (
      <Modal
         isOpen={isOpen}
         onClose={handleClose}
         title="Restaurar item cancelado"
         maxWidth="480px"
      >
         <Body>
            <QuestionLine>
               Tem certeza que deseja restaurar o item{' '}
               <strong>
                  {item.order_number
                     ? `pedido ${item.order_number}`
                     : `#${item.id}`}
               </strong>
               ?
            </QuestionLine>

            <HintBox>
               O item volta ao status <strong>Pendente</strong>, no balde{' '}
               <strong>Não roteirizados</strong> da roteirização. Os metadados
               do cancelamento (data, autor e motivo) são apagados — se precisar
               excluir de novo, um novo motivo será exigido.
            </HintBox>

            <ButtonRow>
               <GhostButton
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
               >
                  Voltar
               </GhostButton>
               <PrimaryButton
                  type="button"
                  onClick={handleConfirm}
                  disabled={isSubmitting}
               >
                  {isSubmitting ? 'Restaurando…' : 'Sim, restaurar item'}
               </PrimaryButton>
            </ButtonRow>
         </Body>
      </Modal>
   );
}

RestoreItemDialog.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   // { id, order_number } — o mínimo pro texto e pro POST /restore.
   item: PropTypes.object,
   onRestored: PropTypes.func,
};
